import type { ServiceKey } from "@/lib/routes";

export { formatEur } from "@/lib/format";

/**
 * Static safety fallback for individual services. The public catalogue and
 * Stripe normally use the prices stored in the CRM. Each fallback service is
 * priced in one of three modes:
 *
 * - "one_time"              -> only a one-off setup fee ("una tantum / avvio")
 * - "one_time_plus_monthly" -> a setup fee plus a recurring monthly fee
 * - "monthly"               -> only a recurring monthly fee ("solo canone")
 */
export type PricingMode = "one_time" | "one_time_plus_monthly" | "monthly";

export type ServicePricing = {
  mode: PricingMode;
  /** One-off setup fee in EUR (VAT excluded). */
  setup?: number;
  /** Recurring monthly fee in EUR (VAT excluded). */
  monthly?: number;
  /**
   * When true the amounts are shown as "starting from" (a partire da),
   * signalling that the final quote depends on the project.
   */
  from?: boolean;
};

/**
 * Fallback usato esclusivamente mentre il catalogo CRM non è disponibile:
 * - una tantum: grafica, plugin WordPress, moduli PrestaShop, template WordPress
 * - una tantum + canone: siti web, e-commerce, web app
 * - solo canone: social & marketing, SEO
 * - su preventivo: stampa digitale e coworking (assenti dalla mappa)
 *
 * I prezzi pubblicati e addebitabili restano sempre quelli del catalogo CRM.
 */
export const SERVICE_PRICING: Partial<Record<ServiceKey, ServicePricing>> = {
  // Una tantum (solo avvio)
  graphics: { mode: "one_time", setup: 250, from: true },
  wpplugin: { mode: "one_time", setup: 400, from: true },
  psmodule: { mode: "one_time", setup: 450, from: true },
  wptheme: { mode: "one_time", setup: 600, from: true },

  // Una tantum + canone mensile
  web: { mode: "one_time_plus_monthly", setup: 900, monthly: 30, from: true },
  webapp: {
    mode: "one_time_plus_monthly",
    setup: 2500,
    monthly: 90,
    from: true,
  },
  ecommerce: {
    mode: "one_time_plus_monthly",
    setup: 1800,
    monthly: 60,
    from: true,
  },

  // Solo canone mensile
  marketing: { mode: "monthly", monthly: 300, from: true },
  seo: { mode: "monthly", monthly: 250, from: true },
};

export function getServicePricing(key: ServiceKey): ServicePricing | null {
  return SERVICE_PRICING[key] ?? null;
}
